/**
 * Customer Stories Cards JavaScript
 * Handles card carousel with main card + slices of off-screen cards.
 * On medium screens+: shows 1 main card (70%) with 10% slices of adjacent cards.
 * On small screens: full-width cards with single peek.
 */

document.addEventListener('DOMContentLoaded', function() {
	const animationDuration = 0.4;
	const animationStyle = 'ease';

	const scrollContainer = document.querySelector('[data-cards-scroll]');
	const track = scrollContainer?.querySelector('.customer-stories-cards-track');
	const scrollLeftBtn = document.querySelector('.customer-stories-scroll-left');
	const scrollRightBtn = document.querySelector('.customer-stories-scroll-right');
	const cards = document.querySelectorAll('.customer-stories-card');

	if (!scrollContainer || !track || !scrollLeftBtn || !scrollRightBtn || !cards.length) return;

	const SLICE_WIDTH_PX = 64; // Fixed 64px width for slice peeks (matches Figma)
	const SLICE_GAP_PX = 16;   // Fixed 16px gap between cards (matches Figma)
	
	let currentIndex = 0;
	let isAnimating = false;
	let isInitialLoad = true;
	let isResizing = false;
	let resizeEndTimer = null;

	function applyLayout() {
		const pageColumn = scrollContainer.closest('.l-container');
		const pageColumnWidth = pageColumn ? pageColumn.offsetWidth : scrollContainer.offsetWidth;

		const section = scrollContainer.closest('#customer-stories-cards');
		if (section) {
			section.classList.add('customer-stories-cards-slices-mode');
			section.style.setProperty('--card-main-width', pageColumnWidth + 'px');
		}
		scrollContainer.classList.add('customer-stories-cards-slices-mode');
		scrollContainer.style.overflowX = 'hidden';
		track.style.transform = 'none';
		track.style.display = 'flex';
		track.style.gap = SLICE_GAP_PX + 'px';

		const sliceW = SLICE_WIDTH_PX;
		const viewportWidth = window.innerWidth;
		// Main card uses full page column width; slices sit outside in gutters
		const mainW = pageColumnWidth;

		const skipTransitions = isInitialLoad || isResizing;
		cards.forEach((card, i) => {
			const w = i === currentIndex ? mainW : sliceW;
			card.style.width = w + 'px';
			card.style.minWidth = w + 'px';
			card.style.flexShrink = '0';
			card.style.transition = skipTransitions ? 'none' : `width ${animationDuration}s ${animationStyle}, min-width ${animationDuration}s ${animationStyle}, opacity ${animationDuration}s ${animationStyle}`;
			card.classList.toggle('current', i === currentIndex);
		});

		// Align main card with page column: main card left edge = pageColumnRect.left
		const sumLeft = currentIndex * (sliceW + SLICE_GAP_PX);
		// Transform: scrollRect.left + translateX + sumLeft = pageColumnRect.left
		const applyTransform = () => {
			const scrollRect = scrollContainer.getBoundingClientRect();
			const pageColumnRect = pageColumn?.getBoundingClientRect();
			// translateX moves track; we want main card at pageColumnRect.left
			let translateX = pageColumnRect
				? pageColumnRect.left - scrollRect.left - sumLeft
				: (viewportWidth - mainW) / 2 - sumLeft;
			// Clamp: don't show empty space on right (main card right edge <= scroll container right)
			const maxTranslateX = scrollRect.width - sumLeft - mainW;
			translateX = Math.min(translateX, maxTranslateX);
			track.style.transform = `translateX(${translateX}px)`;
		};
		track.style.transition = skipTransitions ? 'none' : `transform ${animationDuration}s ${animationStyle}`;
		applyTransform();
		requestAnimationFrame(applyTransform);
	}

	function goToIndex(index) {
		if (isAnimating) return;
		index = Math.max(0, Math.min(index, cards.length - 1));
		if (index === currentIndex) return;

		isAnimating = true;
		currentIndex = index;

		applyLayout();

		setTimeout(() => {
			isAnimating = false;
			updateButtons();
		}, animationDuration * 1000);
	}

	function updateButtons() {
		const lightGrey = '#ccc';
		const darkGrey = '#999';
		const black = '#333';
		const buttonOpacity = '0.5';

		scrollLeftBtn.style.opacity = currentIndex > 0 ? '1' : buttonOpacity;
		scrollLeftBtn.style.borderColor = currentIndex > 0 ? black : lightGrey;
		scrollLeftBtn.style.color = currentIndex > 0 ? black : darkGrey;
		scrollLeftBtn.classList.toggle('disabled', currentIndex === 0);

		scrollRightBtn.style.opacity = currentIndex < cards.length - 1 ? '1' : buttonOpacity;
		scrollRightBtn.style.borderColor = currentIndex < cards.length - 1 ? black : lightGrey;
		scrollRightBtn.style.color = currentIndex < cards.length - 1 ? black : darkGrey;
		scrollRightBtn.classList.toggle('disabled', currentIndex === cards.length - 1);
	}

	applyLayout();
	updateButtons();

	// Mark ready and enable transitions for subsequent interactions
	const section = scrollContainer.closest('#customer-stories-cards');
	if (section) {
		section.classList.add('customer-stories-cards-ready');
	}
	requestAnimationFrame(() => {
		isInitialLoad = false;
	});

	scrollLeftBtn.addEventListener('click', () => goToIndex(currentIndex - 1));
	scrollRightBtn.addEventListener('click', () => goToIndex(currentIndex + 1));

	// Slices mode: click collapsed slice to bring it into view
	cards.forEach((card, i) => {
		card.addEventListener('click', () => {
			if (i !== currentIndex) {
				goToIndex(i);
			}
		});
	});

	window.addEventListener('resize', () => {
		isResizing = true;
		const section = scrollContainer.closest('#customer-stories-cards');
		if (section) section.classList.add('customer-stories-cards-resizing');
		if (resizeEndTimer) clearTimeout(resizeEndTimer);
		resizeEndTimer = setTimeout(() => {
			applyLayout(); // Final layout with settled dimensions
			isResizing = false;
			if (section) section.classList.remove('customer-stories-cards-resizing');
			resizeEndTimer = null;
		}, 150);

		currentIndex = 0;

		applyLayout();

		// Layout may not have settled yet; re-apply transform after browser has reflowed
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const pageColumn = scrollContainer.closest('.l-container');
				const pageColumnWidth = pageColumn ? pageColumn.offsetWidth : scrollContainer.offsetWidth;
				const sliceW = SLICE_WIDTH_PX;
				const mainW = pageColumnWidth;
				const sumLeft = currentIndex * (sliceW + SLICE_GAP_PX);
				const scrollRect = scrollContainer.getBoundingClientRect();
				const pageColumnRect = pageColumn?.getBoundingClientRect();
				let translateX = pageColumnRect
					? pageColumnRect.left - scrollRect.left - sumLeft
					: (window.innerWidth - mainW) / 2 - sumLeft;
				const maxTranslateX = scrollRect.width - sumLeft - mainW;
				translateX = Math.min(translateX, maxTranslateX);
				track.style.transform = `translateX(${translateX}px)`;
			});
		});

		updateButtons();
	});

	scrollContainer.style.cssText += 'scrollbar-width: none; -ms-overflow-style: none;';
	const style = document.createElement('style');
	style.textContent = '.customer-stories-cards-scroll::-webkit-scrollbar { display: none; }';
	document.head.appendChild(style);
});
